import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { debounce } from "@mui/material";

import { RootContext } from "../context/RootContext";
import { LoansContext } from "../context/LoansContext";

import {
  getAutocompleteAddress,
  getBorrowerDetailsBasedOnEmail,
  getGuarantorBasedOnBorrowerId,
  getZipCodeList,
} from "../services/network";

import { ZipCodeSuggestion } from "../types/fieldTypes";
import { ObjectType } from "../types/tableTypes";
import { getAutoCalculatedResults } from "../utils/Common";
import { DEFAULT_AUDIT_COMMENT, getAuditFieldPath } from "../constants/auditFields";

interface Field {
  id: string;
  getFieldErrors?: (value: any) => string;
  [key: string]: any;
}

interface Unit {
  [key: string]: any;
}

interface Property {
  propertyLocation?: any;
  propertyInformation?: any;
  propertyEconomics?: any;
  unitInformation?: Unit[];
  [key: string]: any;
}

interface DetailedLoanSizingData {
  propertyDetails?: Property[];
  [key: string]: any;
}

// Constants
const PROPERTY_LEVEL_SECTIONS = [
  "propertyLocation",
  "propertyInformation",
  "propertyEconomics",
  "unitInformation",
] as const;

const PROPERTY_LOCATION_FIELDS = ["postalCode", "address"] as const;

// Credit-Committee-only manual pricing overrides (loanEconomics.interestRate /
// .finalToorakPrice — see LoanEconomics.ts's update_pricing-gated fields) get
// sent to the rules engine as loanPricing.actualInterestRate /
// .finalToorakPrice (src/utils/v2/common.ts). Once set, they're only valid
// for the inputs they were set against — editing anything else on the loan
// re-triggers a full rules-engine evaluation anyway, so a stale manual
// override would silently keep overriding a recalculated result. Editing any
// field OTHER than these two clears them instead, and records the clear as a
// real audit edit so the prior value stays visible in that field's audit
// trail.
const PRICING_OVERRIDE_FIELDS = ["interestRate", "finalToorakPrice"] as const;
const PRICING_OVERRIDE_SECTION = "loanEconomics";

// Mirrors the exact display-string derivation updatePostalOrAddress below
// applies to detailedLoanSizingData, so an audit entry for address/postalCode
// (see getAuditDisplayValue) always matches what the field itself actually
// gets set to — not the raw Smarty Streets suggestion object the Autocomplete
// hands back, which would otherwise render as "[object Object]" in the audit
// trail (AuditTag.tsx).
const getAddressFieldDisplayValue = (value: any, isPostalCode: boolean): any => {
  if (isPostalCode) return value?.zipCode || value;
  const { street_line, city, state } = value || {};
  return street_line && city && state ? `${street_line} ${city} ${state}` : value;
};

const SPECIAL_FIELD_UPDATES = {
  interestOnlyFlag: (value: string, id?: number) => ({
    ioPeriod: value === "Yes" ? "120" : null,
  }),
  prepaymentPenaltyTypeMonths: (value: any, id?: number) => ({
    prepayPenaltyType: value === 0 ? 0 : null,
  }),
  numberOfProperties: (value: any, id?: number) => ({
    crossCollaterlization: value > 1 ? "Y" : "N",
  }),
  loanPurpose: (value: string, id?: number) => ({
    cashOutFlag: value === "Purchase" ? "No Cash Out" : "No Cash",
  }),
};

export const useLoanSizingHandler = () => {
  const { dynamicConfig } = useContext(RootContext);
  const {
    detailedLoanSizingData,
    setDetailedLoanSizingData,
    setNewDebouncedValue,
    detailedLoanSizingErrors,
    setDetailedLoanSizingErrors,
    addressOptions,
    setAddressOptions,
    setZipCodeOptions,
    setBorrowerOptions,
    setGuarantorOptions,
    guarantorOptions,
    borrowerOptions,
    loanDetails,
    updateDetailedLoanSizingData,
    pendingAuditEdits,
    setPendingAuditEdits,
  } = useContext(LoansContext);

  const [zipCodeInputValue, setZipCodeInputValue] = useState<string>("");
  const [addressInputValue, setAddressInputValue] = useState<string>("");
  const [borrowerNameInputValue, setBorrowerNameInputValue] =
    useState<string>("");
  const [guarantorNameInputValue, setGuarantorNameInputValue] =
    useState<string>("");

  const fetchAddress = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getAutocompleteAddress(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        setAddressOptions([...data.suggestions]);
      } catch (err) {
        console.error(err);
      }
    }, 300),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setAddressOptions],
  );

  const fetchZipCodeSuggestions = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getZipCodeList(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        const { city_states, zipcodes } = data[0];
        let newOptions = [] as ZipCodeSuggestion[];
        if (city_states && zipcodes) {
          const zipCode = zipcodes[0].zipcode;
          const temp = city_states.map((ele: ObjectType) => ({
            ...ele,
            zipCode,
          }));
          newOptions = [...newOptions, ...temp];
        }
        setZipCodeOptions(newOptions);
      } catch (err) {
        console.error(err);
      }
    }, 200),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setZipCodeOptions],
  );

  const updatePostalOrAddress = useCallback(
    (
      value: any,
      propertyId: number,
      section: string,
      id: string,
      isPostalCode?: boolean,
    ) => {
      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];
      const { city, state, zipcode } = value || {};
      setAddressOptions(value ? [value, ...addressOptions] : addressOptions);

      let objToReturn: any = {};
      if (isPostalCode) {
        setZipCodeInputValue(value);
        objToReturn = {
          [id]: getAddressFieldDisplayValue(value, true),
          city: value?.city || "",
          state: value?.state_abbreviation || "",
        };
      } else {
        setAddressInputValue(value);
        objToReturn = {
          address: getAddressFieldDisplayValue(value, false),
          city: city || "",
          state: state || "",
          postalCode: zipcode || "",
        };
      }

      if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
        tempPropertyDetails = tempPropertyDetails.map(
          (property: any, index: number) => {
            if (index === propertyId) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  ...objToReturn,
                },
              };
            }
            return property;
          },
        );
      }

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });
    },
    [
      detailedLoanSizingData,
      addressOptions,
      setAddressOptions,
      setDetailedLoanSizingData,
    ],
  );

  const borrowerFieldMap: Record<string, (value: any) => Record<string, any>> =
    {
      borrowingEntityName: (value) => ({
        borrowingEntityName: value?.fullName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
      }),
      borrowerName: (value) => ({
        borrowerName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
      }),
      guarantorName: (value) => ({
        guarantorName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        guarantorEmail: value?.email ?? null,
        guarantorId: value?.borrowerId ?? null,
      }),
    };

  // handleChange's `value` is frequently not what the user actually sees:
  // a dropDown's value is a raw code (e.g. citizenshipStatus's options store
  // "Yes"/"No" under a translated label like "US Citizen" — see DropDown.tsx's
  // t(ele.label)), and an autoComplete's value is the whole raw suggestion
  // object (a Smarty Streets address suggestion, or a borrower/guarantor
  // option). Recording that raw value as this field's audit entry is what
  // made the audit trail (AuditTag.tsx) show "No" for a citizenship dropdown
  // or literally "[object Object]" for an address — this derives the same
  // display value the field itself resolves to (reusing the exact same
  // mappers `applyGeneralSectionChange`/`updatePostalOrAddress` already use)
  // so the two can never drift apart.
  function getAuditDisplayValue(value: any, type: string, field: Field, id: string): any {
    if (PROPERTY_LOCATION_FIELDS.includes(id as any)) {
      return getAddressFieldDisplayValue(value, id === "postalCode");
    }
    if (borrowerFieldMap[id]) {
      return borrowerFieldMap[id](value)?.[id];
    }
    if (type === "dropDown" && Array.isArray(field?.data)) {
      // label is an i18n key, same as what DropDown.tsx renders — AuditTag
      // translates it the same way for display.
      const matchedOption = field.data.find((option: any) => option?.value === value);
      if (matchedOption?.label) return matchedOption.label;
    }
    return value;
  }

  // Applies a change to a property-level (or unit-level) field, including
  // the propertyEconomics auto-calculated costBasis/costBasisCalculated
  // side-effects. Extracted from handleChange to keep its complexity low.
  function applyPropertyLevelSectionChange(
    value: any,
    section: string,
    id: string,
    propertyId: number,
    unitId: number,
  ) {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];

    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            if (unitId >= 0 && unitId < property?.unitInformation?.length) {
              const currentUnitInfo = property[section]?.map(
                (unit: any, uId: number) =>
                  uId === unitId ? { ...unit, [id]: value } : unit,
              );
              return {
                ...property,
                [section]: currentUnitInfo,
              };
            }
            if (
              section === "propertyEconomics" &&
              [
                "borrowerPaidCostToDate",
                "purchasePrice",
                "closingCosts",
              ]?.includes(id)
            ) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  [id]: value,
                  ["costBasis"]: getAutoCalculatedResults(
                    "costBasis",
                    {
                      ...property,
                      [section]: { ...property[section], [id]: value },
                    },
                    {
                      isMultifamily:
                        loanDetails?.propertyType ===
                        "Multifamily/MixedUse",
                    },
                  ),
                  ["costBasisCalculated"]: id === "purchasePrice" ? +(value || 0) + +(property?.propertyEconomics?.verifiedCostsAndImprovements || 0) : property?.propertyEconomics?.costBasisCalculated
                },
              };
            }
            if (section === "propertyEconomics" && ["verifiedCostsAndImprovements"]?.includes(id)) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  [id]: value,
                  ["costBasisCalculated"]: +(property?.propertyEconomics?.propertyAcquisitionPrice || property?.propertyEconomics?.purchasePrice || 0) + +(value || 0)
                },
              };
            }
            return {
              ...property,
              [section]: {
                ...property[section],
                [id]: value,
              },
            };
          }
          return property;
        },
      );
    }

    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  }

  // Applies a change to a non-property-level field, including the
  // borrower/guarantor field-mapping and special-field-update side-effects.
  // Extracted from handleChange to keep its complexity low.
  function applyGeneralSectionChange(
    value: any,
    field: Field,
    section: string,
    id: string,
    propertyId: number,
  ) {
    let returnObj = {
      ...detailedLoanSizingData,
      [section]: {
        ...detailedLoanSizingData?.[section],
        [id]: value,
      },
    };

    if (id === "borrowerType") {
      // Borrower Type is a structural switch (Entity <-> Individual, each
      // with its own set of fields — see BorrowerInformation.ts's isVisible
      // gates) rather than an ordinary field edit. Nulling an enumerated
      // list of "the fields that mattered for the old type" reliably missed
      // fields as the section grew over time (TA-1234) — e.g. stale
      // borrowingEntityName/guarantor data survived a switch to Individual
      // and broke submission. Replacing the whole section can't miss
      // anything that way; also clears its validation errors, since a
      // leftover "required" error for a field that's no longer even shown
      // was part of the same submit-time breakage.
      returnObj = {
        ...detailedLoanSizingData,
        [section]: { borrowerType: value },
      };
      setBorrowerOptions([]);
      setGuarantorOptions([]);
      setBorrowerNameInputValue("");
      setGuarantorNameInputValue("");
      setDetailedLoanSizingErrors({
        ...detailedLoanSizingErrors,
        [section]: {},
      });
    } else if (borrowerFieldMap[id]) {
      returnObj = {
        ...detailedLoanSizingData,
        [section]: {
          ...detailedLoanSizingData?.[section],
          ...borrowerFieldMap[id](value), // spreads the mapped fields
        },
      };
    }
    const specialUpdate =
      SPECIAL_FIELD_UPDATES[id as keyof typeof SPECIAL_FIELD_UPDATES];
    if (specialUpdate) {
      returnObj = {
        ...returnObj,
        [section]: {
          ...returnObj[section],
          ...specialUpdate(value, propertyId),
        },
      };
    }
    if (id === "borrowingEntityName") {
      getGuarantorDetails(
        value?.borrowerId,
        dynamicConfig?.fileUploadUrl,
        returnObj,
      );
    } else {
      setDetailedLoanSizingData(returnObj);
    }
  }

  // Clears any stale Credit-Committee pricing overrides (see
  // PRICING_OVERRIDE_FIELDS comment above) unless the field being edited is
  // itself one of the override fields. Extracted from handleChange to keep
  // its complexity low.
  function clearStalePricingOverrides(section: string, id: string) {
    const isEditingPricingOverrideField =
      section === PRICING_OVERRIDE_SECTION &&
      (PRICING_OVERRIDE_FIELDS as readonly string[]).includes(id);
    if (isEditingPricingOverrideField) return;

    const staleOverrideKeys = PRICING_OVERRIDE_FIELDS.filter(
      (key) => detailedLoanSizingData?.loanEconomics?.[key],
    );
    if (!staleOverrideKeys.length) return;

    setPendingAuditEdits?.((prevEdits: any) => {
      const cleared = { ...prevEdits };
      staleOverrideKeys.forEach((key) => {
        cleared[getAuditFieldPath(PRICING_OVERRIDE_SECTION, key)] = {
          value: "",
          comment: DEFAULT_AUDIT_COMMENT,
        };
      });
      return cleared;
    });
    setDetailedLoanSizingData((prev: any) => ({
      ...prev,
      loanEconomics: {
        ...prev?.loanEconomics,
        ...Object.fromEntries(staleOverrideKeys.map((key) => [key, ""])),
      },
    }));
  }

  const handleChange = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const { id } = field;

      const auditPath = getAuditFieldPath(section, id, propertyId, unitId);
      setPendingAuditEdits?.({
        ...pendingAuditEdits,
        [auditPath]: {
          value: getAuditDisplayValue(value, type, field, id),
          comment: DEFAULT_AUDIT_COMMENT,
        },
      });

      if (PROPERTY_LOCATION_FIELDS.includes(id as any)) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        applyPropertyLevelSectionChange(value, section, id, propertyId, unitId);
      } else {
        applyGeneralSectionChange(value, field, section, id, propertyId);
      }

      clearStalePricingOverrides(section, id);

      if (["dropDown", "autoComplete"].includes(type)) {
        onBlur(value, type, field, section, propertyId, unitId);
      }
      setNewDebouncedValue(value);
    },
    [
      detailedLoanSizingData,
      setDetailedLoanSizingData,
      updatePostalOrAddress,
      setNewDebouncedValue,
      pendingAuditEdits,
      setPendingAuditEdits,
    ],
  );

  const onBlur = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const errorMessage: any = field?.getFieldErrors?.(value);
      const { id } = field;

      if (
        PROPERTY_LOCATION_FIELDS.includes(
          id as (typeof PROPERTY_LOCATION_FIELDS)[number],
        )
      ) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        let tempPropertyDetails = [
          ...(detailedLoanSizingErrors?.propertyDetails || [{}]),
        ];

        if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
          tempPropertyDetails = tempPropertyDetails.map(
            (property: any, index: number) => {
              if (index === propertyId) {
                if (unitId >= 0 && unitId < property?.unitInformation?.length) {
                  const currentUnitInfo = property[section]?.map(
                    (unit: any, uId: number) =>
                      uId === unitId ? { ...unit, [id]: errorMessage } : unit,
                  );
                  return {
                    ...property,
                    [section]: currentUnitInfo,
                  };
                }
                return {
                  ...property,
                  [section]: {
                    ...property[section],
                    [id]: errorMessage,
                  },
                };
              }
              return property;
            },
          );
        }

        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          propertyDetails: tempPropertyDetails,
        });
      } else {
        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          [section]: {
            ...detailedLoanSizingErrors?.[section],
            [id]: errorMessage,
          },
        });
      }
    },
    [
      detailedLoanSizingErrors,
      updatePostalOrAddress,
      setDetailedLoanSizingErrors,
    ],
  );

  const debouncedBorrowerFetch = useMemo(
    () => debounce((borrowerObj: any) => getBorrowers(borrowerObj), 3000),
    [],
  );

  async function getBorrowers(borrowerObj: any) {
    try {
      if (!borrowerObj.username || !borrowerObj?.borrowerType) return;
      const payload = {
        username: borrowerObj.username,
        tenant: "sizer_to_los",
        borrowerType: borrowerObj?.borrowerType?.toUpperCase(),
        isTestParty: false,
      };
      const data = await getBorrowerDetailsBasedOnEmail(payload);
      if (borrowerObj?.borrowerType === "INDIVIDUAL") {
        setGuarantorOptions(data?.data ?? []);
      } else {
        setBorrowerOptions(data?.data ?? []);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function getGuarantorDetails(
    borrowerId: string,
    url: string,
    returnObj: any,
  ) {
    try {
      if (!borrowerId) return;
      const { data } = await getGuarantorBasedOnBorrowerId(
        borrowerId,
        url?.split("/bff")[0] ?? "",
      );
      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        ...returnObj,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          ...returnObj.borrowerInformation,
          guarantorName: data?.data?.[0]?.fullName || "",
          guarantorEmail: data?.data?.[0]?.email || "",
          guarantorId: data?.data?.[0]?.borrowerId || "",
        },
      });
    } catch (err) {
      console.error(err);
    }
  }
  useEffect(() => {
    if (zipCodeInputValue.length >= 4 && zipCodeInputValue.length <= 7) {
      fetchZipCodeSuggestions({ input: zipCodeInputValue });
    }
  }, [zipCodeInputValue, fetchZipCodeSuggestions]);

  useEffect(() => {
    if (addressInputValue) {
      fetchAddress({ input: addressInputValue });
    }
  }, [addressInputValue, fetchAddress]);

  useEffect(() => {
    const borrowerType =
      detailedLoanSizingData?.borrowerInformation?.borrowerType;
    if (borrowerNameInputValue) {
      debouncedBorrowerFetch({
        username: borrowerNameInputValue,
        borrowerType: borrowerType,
      });
    }
  }, [borrowerNameInputValue, debouncedBorrowerFetch]);

  useEffect(() => {
    if (guarantorNameInputValue) {
      debouncedBorrowerFetch({
        username: guarantorNameInputValue,
        borrowerType: "INDIVIDUAL",
      });
    }
  }, [guarantorNameInputValue, debouncedBorrowerFetch]);

  return {
    handleChange,
    onBlur,
    updatePostalOrAddress,
    addressOrPostalOnInputChange: (
      value: any,
      type: any,
      field: any,
      section: any,
      propertyId: any,
    ) => {
      const { id } = field;
      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];

      tempPropertyDetails = tempPropertyDetails.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              [section]: {
                ...property[section],
                [id]: value,
              },
            };
          }
          return property;
        },
      );

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });

      if (field.id === "postalCode") {
        setZipCodeInputValue(value);
      } else {
        setAddressInputValue(value);
      }
    },
    borrowerOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["borrowingEntityName", "borrowerName"].includes(id)) {
        updated.borrowerInformation = {
          ...updated.borrowerInformation,
          id:
            borrowerOptions?.find((g: any) => [value].includes(g.fullName))
              ?.borrowerId ?? null,
        };
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setBorrowerNameInputValue(value);
    },
    guarantorOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["guarantorEmail", "guarantorName"].includes(id)) {
        updated.borrowerInformation = {
          ...updated.borrowerInformation,
          guarantorId:
            guarantorOptions?.find(
              (g: any) =>
                g.email === updated.borrowerInformation.guarantorEmail &&
                g.fullName === updated.borrowerInformation.guarantorName,
            )?.borrowerId ?? null,
        };
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setGuarantorNameInputValue(value);
    },
  };
};
